import axios, {AxiosError, AxiosProgressEvent, AxiosRequestConfig, RawAxiosRequestHeaders} from 'axios';
import {isBoolean, isObject, isObjectLike, pickBy} from 'lodash';

import {globalWindow} from '../../helpers/browser/browserHelpers';
import {FrontConfig} from '../../helpers/bundler/bundlerDefineConfig';
import {isDevelopment} from '../../helpers/environment/envHelpers';
import {isMobile} from '../../helpers/environment/mobileEnvHelpers';
import {annotateTaskType, TaskTypesEnum} from '../../helpers/instrumentation/eventLoopPerformanceHelpers';
import {isDefined} from '../../helpers/typescript/typeHelpers';
import {CancelError} from '../../helpers/utilities/asyncCancelHelpers';
import {trace} from '../debug/debug';
import {getBuildVersion} from '../globals/buildVersion';
import {logger} from '../logger/logger';
import {HttpNetworkError, HttpResponseError, HttpTimeoutError} from './httpError';
import {HttpFullSendOptions, HttpResponse, RequestMethodsEnum} from './httpTypes';
import {httpDefaultHeaders, httpUserAgent} from './mobileHttpHeaders';
import {sessionId} from './session';

/*
 * HACK: Find better solution.
 */

type PushGlobalState = {
  defaultSocketId: string | null;
  ablySocketId: string | null;
};
export const pushState: PushGlobalState = {defaultSocketId: null, ablySocketId: null};

/*
 * Setup.
 */

let authenticationToken: string | undefined;
export function setHttpToken(token: string | undefined) {
  authenticationToken = token;
}

export function httpToken() {
  return authenticationToken;
}

let baseUrl: string | undefined;
export function setBaseUrl(newBaseUrl: string | undefined) {
  baseUrl = newBaseUrl;
}

let isTracingPerformance = false;
export function setIsTracingPerformance(isTracing: boolean) {
  isTracingPerformance = isTracing;
}

let requestTraceTag: string | undefined;
export function setRequestTraceTag(tag: string | undefined) {
  // Filter out empty strings.
  requestTraceTag = tag || undefined;
}

const requester = axios.create({
  baseURL: FrontConfig.API_URL,
  // We don't make cross-origin requests. Mitigates https://security.snyk.io/vuln/SNYK-JS-AXIOS-6032459
  withCredentials: isMobile(),
  xsrfCookieName: 'front.csrf',
  xsrfHeaderName: 'X-Front-Xsrf',
  headers: httpDefaultHeaders,
});

/*
 * API.
 */

export function buildHttpRequestHeaders() {
  const majorBuildVersion = getBuildVersion();

  const headers = pickBy(
    {
      Accept: 'application/json',
      Authorization: authenticationToken ? `Bearer ${authenticationToken}` : undefined,
      'user-agent': httpUserAgent,
      // TODO: Deprecate this header in favor of x-front-webapp-*.
      'X-Front-Version': `${majorBuildVersion}-${FrontConfig.GIT_COMMIT}`,
      'X-Front-Trace-Perf': isTracingPerformance ? 'yes' : 'no',
      'X-Front-Webapp-Build-Timestamp': FrontConfig.BUILD_TIMESTAMP,
      'X-Front-Webapp-Commit': FrontConfig.GIT_COMMIT,
      'X-Front-Trace-Tag': requestTraceTag,
    },
    isDefined,
  );

  // Add WebSocket IDs if available.
  if (pushState.defaultSocketId) {
    headers['X-Front-WebSocket-Id'] = pushState.defaultSocketId;
  }

  if (pushState.ablySocketId) {
    headers['X-Front-Ably-WebSocket-Id'] = pushState.ablySocketId;
  }

  // Add precogs and session ID.
  headers['x-front-precogs'] = 'direct';
  headers['X-Front-Session-Id'] = sessionId;

  return headers satisfies RawAxiosRequestHeaders;
}

/** Perform a generic HTTP method. */
export async function sendAsync(options: HttpFullSendOptions): Promise<HttpResponse> {
  // Build the options for our request.
  const headers = buildHttpRequestHeaders();
  const {method, path, body, cancelToken, timeout, errorHandlers} = options;

  if (isMobile()) {
    logger.debug('Performing request:', {path: options.path});
  }

  const abortController = new AbortController();
  const onAbort = () => {
    abortController.abort();
  };

  const cancelSubscription = cancelToken?.subscribe(onAbort);
  options.signal?.addEventListener('abort', onAbort);

  const requestStartTime = Date.now();
  const timeoutDelay = timeout || 30000;

  const axiosOptions: AxiosRequestConfig = {
    baseURL: (!options.skipBaseUrl && baseUrl) || undefined,
    url: path,
    method,
    headers,
    responseType: options.responseType,
    signal: abortController.signal,
    onUploadProgress: (e) => onProgress(e, options),
    onDownloadProgress: (e) => onProgress(e, options),
    // On mobile, we use abortController instead of the Axios built-in timeout.
    timeout: isMobile() ? 0 : timeoutDelay,
  };

  // Axios' timeout is not completely reliable, so we made our own.
  let didTimeout = false;
  const timeoutSubscription = scheduleTimeout(() => {
    didTimeout = true;
    abortController.abort();
  }, timeoutDelay);

  // If a body was provided, add it.
  if (body) {
    axiosOptions.data = body;
  }

  // Merge any custom headers from options.
  Object.assign(headers, options.headers);

  trace(`http-${method}-start`, {path, body});

  try {
    // Perform the request.
    const response = await runAsyncWithAnnotation(() => requester.request(axiosOptions));

    trace(`http-${method}-end`, {path, body});

    if (options.onResponseReceived) {
      options.onResponseReceived({
        headers: response.headers || {},
      });
    }

    return response;
  } catch (error) {
    if (!axios.isAxiosError(error)) {
      throw error;
    }

    if (didTimeout || isAxiosTimeoutError(error)) {
      throw new HttpTimeoutError(method, path, Date.now() - requestStartTime);
    }

    // If this is a cancel error, return our own.
    if (axios.isCancel(error) || isAxiosAbortedError(error)) {
      throw new CancelError();
    }

    // If this is an XHR network error, wrap in our own class.
    // https://github.com/axios/axios/blob/master/lib/adapters/xhr.js#L81
    if (isAxiosNetworkError(error)) {
      throw new HttpNetworkError(method, path);
    }

    trace(`http-${method}-error`, {path, body, error});

    if (errorHandlers && error.response?.status === 401) {
      if (
        isObject(error.response.data) &&
        'code' in error.response.data &&
        error.response.data.code === 'session_idle_timeout'
      ) {
        errorHandlers.onSessionTimeout();
      } else if (
        isObject(error.response.data) &&
        'code' in error.response.data &&
        error.response.data.code === 'session_max_limit'
      ) {
        errorHandlers.onSessionsMaxReached();
      } else {
        errorHandlers.onUnauthorized();
      }
    }

    // If this is an HTTP error, wrap in our own class.
    // Check the status to work around https://github.com/axios/axios/issues/4655
    if (error.response?.status) {
      throw new HttpResponseError(method, path, error.response);
    }

    // No idea what this is, so let's rethrow.
    throw error;
  } finally {
    cancelSubscription?.unsubscribe();
    options.signal?.removeEventListener('abort', onAbort);
    timeoutSubscription.unsubscribe();
  }
}

async function runAsyncWithAnnotation<T>(callback: () => Promise<T>): Promise<T> {
  try {
    return await callback();
  } finally {
    annotateTaskType(TaskTypesEnum.NETWORK_RESPONSE);
  }
}

function isProgressEvent(src: any): src is ProgressEvent {
  if (typeof ProgressEvent !== 'undefined') {
    return src instanceof ProgressEvent;
  }

  // React Native doesn't expose ProgressEvent so we can't always rely on instanceof.
  return (
    isObjectLike(src) &&
    isBoolean(src.lengthComputable) &&
    Number.isFinite(src.loaded) &&
    Number.isFinite(src.total)
  );
}

function onProgress(axiosEvent: AxiosProgressEvent, options: HttpFullSendOptions) {
  const {onProgressChanged} = options;
  if (!onProgressChanged) {
    return;
  }

  if (isProgressEvent(axiosEvent.event)) {
    onProgressChanged(axiosEvent.event);
  }
}

/*
 * Spying.
 */

export function spyOnHttpResponses(
  onResponse: (response: HttpResponse) => void,
  onNetworkError?: (arg: HttpFullSendOptions | undefined) => void,
) {
  const interceptor = requester.interceptors.response.use(
    (response) => {
      onResponse(response);
      return response;
    },
    (error) => {
      if (onNetworkError && isAxiosDevServerNetworkError(error)) {
        onNetworkError(createSendOptionsFromError(error));
      }
      // Check the status to work around https://github.com/axios/axios/issues/4655
      else if (error.response?.status) {
        onResponse(error.response);
      } else if (onNetworkError && (isAxiosNetworkError(error) || isAxiosTimeoutError(error))) {
        onNetworkError(createSendOptionsFromError(error));
      }

      return Promise.reject(error);
    },
  );
  return () => requester.interceptors.response.eject(interceptor);
}

/*
 * Helpers.
 */

function isAxiosDevServerNetworkError(error: AxiosError) {
  // This happens when the local webpack dev server cannot proxy to the API server.
  return isDevelopment() && error.response?.status === 504;
}

/** Detects the error thrown when the browser cancels the request. */
export function isAxiosAbortedError(error: AxiosError) {
  return error.code === AxiosError.ECONNABORTED && !error.message.startsWith('timeout');
}

export function isAxiosNetworkError(error: AxiosError) {
  return error.code === AxiosError.ERR_NETWORK;
}

export function isAxiosTimeoutError(error: AxiosError) {
  return (
    error.code === AxiosError.ETIMEDOUT ||
    (error.code === AxiosError.ECONNABORTED && error.message.startsWith('timeout'))
  );
}

function createSendOptionsFromError(error: unknown): HttpFullSendOptions | undefined {
  if (!axios.isAxiosError(error) || !error.config) {
    return undefined;
  }

  const {method, url} = error.config;
  if (!method || !url) {
    return undefined;
  }

  return {
    method: method.toUpperCase() as RequestMethodsEnum,
    path: url,
  };
}

function scheduleTimeout(callback: () => void, timeout: number) {
  let mobileTimeoutId: number | undefined;
  const timeoutId = globalWindow.setInstrumentedTimeout(() => {
    if (isMobile()) {
      // On mobile, give the application some time to process the event queue
      // before executing the callback.
      // See https://front.atlassian.net/browse/PB-33685
      mobileTimeoutId = globalWindow.setInstrumentedTimeout(callback, 200);
      return;
    }

    callback();
  }, timeout);

  return {
    unsubscribe() {
      globalWindow.clearTimeout(timeoutId);
      globalWindow.clearTimeout(mobileTimeoutId);
    },
  };
}

declare module 'axios' {
  interface Cancel {
    readonly __CANCEL__: true;
  }
}
